import { useState } from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { check1on1TranscriptionEnabled } from 'utils/helpers/featureFlags';

import { InitiativesIconDualTone, Plus } from 'assets/assetsV1/iconComponents';

import { sendClickedOnAddInitiativeEvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';

import { CardTemplate } from '../../common/CardTemplate';
import SuggestionsTemplate from '../../common/SuggestionsTemplate';
import { oneOneOneReactQueryKeys } from '../../constants/oneOnOneReactQueryKeys';
import { useOneOnOneInstanceDetails } from '../../hooks/dataHooks';
import { useOneOnOneBotRecommendedDataQuery } from '../Agenda/hooks/dataHooks';
import { deleteOneOnOneAiRecommendedData } from '../Agenda/services';
import { createActionItemObject, CreateInitiative } from './components/CreateInitiative';
import { InitiativeItem } from './components/InitiativeItem';
import MlSuggestions from './components/MlSuggestions';
import { useCreateActionItemMutation, useOneOnOneActionItemsQuery } from './hooks/dataHook';

const InitiativesList = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();
  const [showList, setShowList] = useState(true);
  const queryClient = useQueryClient();
  const { data: actionItems } = useOneOnOneActionItemsQuery({
    masterId,
    instanceId,
  });
  const createActionItemMutation = useCreateActionItemMutation({
    masterId,
    instanceId,
  });
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  const [creatingActionItem, setCreatingActionItem] = useState(false);

  const { data } = useOneOnOneBotRecommendedDataQuery({
    masterId,
    instanceId,
  });

  const { completionStatus } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const { aigeneratedActionItems } = data ?? {};

  const addAiGeneratedInitiatives = async (aiInitiativeText) => {
    try {
      const newActionItem = createActionItemObject({
        agendaText: aiInitiativeText?.generatedText,
        displayName: loggedInEmployeeData?.displayName,
        profilePhoto: loggedInEmployeeData?.profilePhotoSrc,
        designation: loggedInEmployeeData?.designation,
        employeeFunction: loggedInEmployeeData?.employeeFunction,
        userId: loggedInEmployeeData?.id,
      });
      createActionItemMutation.mutate({
        updatedActionItem: newActionItem,
        onMutation: () => setCreatingActionItem(false),
      });
    } catch (e) {
      console.error('Error creating agenda : ', e);
    }
    await deleteOneOnOneAiRecommendedData({
      masterId,
      instanceId,
      aiGeneratedDataId: aiInitiativeText.aiGeneratedDataId,
    });
    queryClient.invalidateQueries([oneOneOneReactQueryKeys.botRecommendedData, masterId, instanceId]);
  };

  const showDivider = !(completionStatus === 'COMPLETED' || completionStatus === 'CANCELLED');

  const showAiGeneratedInitiatives =
    check1on1TranscriptionEnabled() &&
    (completionStatus === 'COMPLETED' || completionStatus === 'CANCELLED') &&
    aigeneratedActionItems?.length > 0;

  return (
    <CardTemplate hasDivider={showDivider} Icon={InitiativesIconDualTone} heading="Initiatives from 1:1">
      {showAiGeneratedInitiatives && (
        <SuggestionsTemplate
          title="Initiatives Identified by Maven"
          subTitle="Additional initiatives discovered by Maven from your 1:1 call"
          showList={showList}
          setShowList={setShowList}
        >
          {aigeneratedActionItems?.map((val) => (
            <MlSuggestions
              key={val?.key}
              title={val.generatedText}
              subTitle="Initiatives"
              handleAdd={() => addAiGeneratedInitiatives(val)}
            />
          ))}
        </SuggestionsTemplate>
      )}

      <Flex flexDir="column" w="100%">
        {actionItems?.map((actionItem) => (
          <>
            <InitiativeItem {...actionItem} />
            <Divider opacity={1} color="#EBEFF3" />
          </>
        ))}
      </Flex>
      {creatingActionItem && <CreateInitiative setCreatingInitiative={setCreatingActionItem} />}
      <Button
        ml="16px"
        mb="16px"
        StartIcon={Plus}
        variant="outlined"
        mt="24px"
        onClick={() => {
          sendClickedOnAddInitiativeEvent({
            eventProperties: {
              instance_id: instanceId,
            },
          });
          setCreatingActionItem(true);
        }}
      >
        Add Initiative
      </Button>
    </CardTemplate>
  );
};

export default InitiativesList;
